// src/pages/SettingsPage.js
// ตั้งค่าระบบ: ข้อมูลร้าน / หัวบิล / ท้ายบิล / โลโก้ / QR / default paper size / Backup Import-Export
// รีแฟคเตอร์ UI: แบ่งคอลัมน์ภายในการ์ดหลักให้กระชับ (2 คอลัมน์บนจอ md+), ลดการเลื่อนหน้าที่ยาวเกิน

import React, { useContext, useEffect, useState } from "react";
import api from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";

const SettingsPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const isAdmin = user?.role === "admin";

  const [settings, setSettings] = useState({
    shop_name: "",
    shop_address: "",
    shop_phone: "",
    bill_header_text: "",
    bill_footer_promo: "",
    bill_logo_url: "",
    bill_qr_image_url: "",
    default_paper_size: "80",
    default_bill_font_size: "0.8rem",
  });

  const [importFile, setImportFile] = useState(null); // Fixed ReferenceError
  const [importSection, setImportSection] = useState('employees'); // section for CSV import
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Wipe all data modal state
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipeCodeInput, setWipeCodeInput] = useState("");
  const expectedWipeCode = 'RESET'; // ต้องพิมพ์คำว่า RESET (backend ตรวจแบบ uppercase) - ตอนนี้รวมลูกค้า (VIP Member) ด้วย
  const [wiping, setWiping] = useState(false);

  // โหลด settings ครั้งแรก
  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get("/settings");
      const data = res.data || {};
      setSettings((prev) => ({
        ...prev,
        shop_name: data.shop_name || "",
        shop_address: data.shop_address || "",
        shop_phone: data.shop_phone || "",
        bill_header_text: data.bill_header_text || "",
        bill_footer_promo: data.bill_footer_promo || "",
        bill_logo_url: data.bill_logo_url || "",
        bill_qr_image_url: data.bill_qr_image_url || "",
        default_paper_size: data.default_paper_size || "80",
        default_bill_font_size: data.default_bill_font_size || "0.8rem",
      }));
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดการตั้งค่าไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await api.put("/settings", settings);
      addNotification("บันทึกการตั้งค่าเรียบร้อยแล้ว", "success");
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกการตั้งค่าไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (type) => {
    if (!isAdmin) return;
    try {
      setExporting(true);
      const res = await api.get("/backup/export", {
        params: { type },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      a.href = url;
      a.download = `beauty-salon-backup-${type}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      addNotification(`Export ข้อมูล (${type}) เสร็จแล้ว`, "success");
    } catch (err) {
      addNotification(
        err.response?.data?.message ||
        "Export ข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        "danger"
      );
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = async (section) => {
    if (!isAdmin) return;
    setExporting(true);
    try {
      const res = await api.get(`/backup/export/csv/${section}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      a.href = url;
      a.download = `backup_${section}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      addNotification(`Export ${section} CSV สำเร็จ`, 'success');
    } catch (err) {
      addNotification(err.response?.data?.message || 'Export CSV ไม่สำเร็จ', 'danger');
    } finally { setExporting(false); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const text = await importFile.text();
      const data = JSON.parse(text);
      const res = await api.post("/backup/import", data);
      addNotification(res.data?.message || "นำเข้าข้อมูลสำเร็จ", "success");
      await loadSettings();
    } catch (err) {
      console.error(err);
      addNotification(
        err.response?.data?.message ||
        "นำเข้าข้อมูลไม่สำเร็จ กรุณาเช็คไฟล์ backup",
        "danger"
      );
    } finally {
      setImporting(false);
    }
  };

  const handleImportCsv = async () => {
    if (!isAdmin || !importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post(`/backup/import/csv/${importSection}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      addNotification(res.data?.message || 'นำเข้า CSV สำเร็จ', 'success');
      setImportFile(null);
    } catch (err) {
      addNotification(err.response?.data?.message || 'นำเข้า CSV ไม่สำเร็จ', 'danger');
    } finally { setImporting(false); }
  };

  const renderPreviewImage = (label, url) => {
    if (!url) return null;
    return (
      <div className="mt-1">
        <small className="text-muted d-block mb-1">{label}</small>
        <img
          src={url}
          alt={label}
          style={{
            maxHeight: 120,
            maxWidth: "100%",
            borderRadius: 8,
            border: "1px solid #eee",
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div className="dashboard-page settings-page container">
        {/* hero */}
        <div className="dashboard-hero mb-3">
          <div>
            <div className="dashboard-hero-title">
              <i className="bi bi-gear me-2"></i>การตั้งค่าระบบ / Settings
            </div>
            <div className="dashboard-hero-subtitle">
              จัดการข้อมูลร้าน รูปแบบใบเสร็จ และสำรองข้อมูลระบบ
            </div>
          </div>
        </div>

        {!isAdmin && (
          <div className="alert alert-warning">
            คุณไม่ได้เป็นผู้ดูแลระบบ (admin) สามารถดูการตั้งค่าได้อย่างเดียว
            ไม่สามารถแก้ไขหรือ Export/Import ข้อมูลได้
          </div>
        )}

        <div className="row">
          {/* การตั้งค่าบิล / ร้าน */}
          <div className="col-lg-8 mb-3">
            <div className="card card-ks-soft">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title m-0 fw-bold text-secondary">
                    <i className="bi bi-shop me-2"></i>ข้อมูลร้าน & รูปแบบใบเสร็จ
                  </h5>
                  <div className="d-flex gap-2">
                    <div style={{ minWidth: 150 }}>
                      <label className="form-label form-label-sm mb-1">
                        ขนาดตัวอักษรเริ่มต้น
                      </label>
                      <select
                        className="form-select form-select-sm"
                        disabled={!isAdmin}
                        value={settings.default_bill_font_size || "0.8rem"}
                        onChange={(e) => handleChange("default_bill_font_size", e.target.value)}
                      >
                        <option value="0.7rem">Font เล็ก (0.7rem)</option>
                        <option value="0.78rem">Font 58mm (0.78rem)</option>
                        <option value="0.8rem">Font 80mm (0.8rem)</option>
                        <option value="0.9rem">Font A4 (0.9rem)</option>
                        <option value="1.0rem">Font ใหญ่ (1.0rem)</option>
                      </select>
                    </div>
                    <div style={{ minWidth: 150 }}>
                      <label className="form-label form-label-sm mb-1">
                        ขนาดกระดาษเริ่มต้น
                      </label>
                      <select
                        className="form-select form-select-sm"
                        disabled={!isAdmin}
                        value={settings.default_paper_size || "80"}
                        onChange={(e) => handleChange("default_paper_size", e.target.value)}
                      >
                        <option value="58">สลิป 58 มม.</option>
                        <option value="80">สลิป 80 มม.</option>
                        <option value="a4">A4 ปกติ</option>
                      </select>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div>กำลังโหลดการตั้งค่า...</div>
                ) : (
                  <>
                    {/* TWO-COLUMN GRID */}
                    <div className="row g-3">
                      {/* LEFT COLUMN */}
                      <div className="col-md-6">
                        <div className="mb-2">
                          <label className="form-label form-label-sm">ชื่อร้าน / Shop Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={settings.shop_name}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("shop_name", e.target.value)}
                            placeholder="เช่น POS88"
                          />
                        </div>

                        <div className="mb-2">
                          <label className="form-label form-label-sm">เบอร์โทรร้าน / Phone</label>
                          <input
                            className="form-control form-control-sm"
                            value={settings.shop_phone}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("shop_phone", e.target.value)}
                            placeholder="เช่น 08x-xxx-xxxx"
                          />
                        </div>

                        <div className="mb-2">
                          <label className="form-label form-label-sm">ที่อยู่ร้าน (แสดงบนบิล)</label>
                          <textarea
                            className="form-control form-control-sm"
                            rows={5}
                            value={settings.shop_address}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("shop_address", e.target.value)}
                            placeholder={"เช่น 123/45 ถนนxxx เขตxxx กรุงเทพฯ 10xxx\n(กด Enter เพื่อขึ้นบรรทัดใหม่)"}
                          />
                        </div>
                      </div>

                      {/* RIGHT COLUMN */}
                      <div className="col-md-6">
                        <div className="mb-2">
                          <label className="form-label form-label-sm">ข้อความหัวบิล / Bill Header</label>
                          <textarea
                            className="form-control form-control-sm"
                            rows={4}
                            value={settings.bill_header_text}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("bill_header_text", e.target.value)}
                            placeholder={"เช่น ยินดีให้บริการ\nขอบคุณที่ใช้บริการร้านเรา..."}
                          />
                          <small className="text-muted">จะแสดงด้านบนของใบเสร็จ (รองรับขึ้นบรรทัดใหม่)</small>
                        </div>

                        <div className="mb-2">
                          <label className="form-label form-label-sm">ข้อความโปรโมชั่นท้ายบิล / Promotion Footer</label>
                          <textarea
                            className="form-control form-control-sm"
                            rows={4}
                            value={settings.bill_footer_promo}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("bill_footer_promo", e.target.value)}
                            placeholder={"เช่น โปรเดือนหน้า\nส่วนลด 10% สำหรับลูกค้าที่กลับมาใช้บริการ..."}
                          />
                          <small className="text-muted">จะแสดงด้านล่างของใบเสร็จ (รองรับขึ้นบรรทัดใหม่)</small>
                        </div>
                      </div>

                      {/* IMAGES & QR (SIDE BY SIDE) */}
                      <div className="col-md-6">
                        <div className="mb-2">
                          <label className="form-label form-label-sm">URL โลโก้ร้าน (สำหรับหัวบิล)</label>
                          <input
                            className="form-control form-control-sm"
                            value={settings.bill_logo_url}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("bill_logo_url", e.target.value)}
                            placeholder="วางลิงก์รูปโลโก้ (เช่น จากการ upload ในเว็บอื่น)"
                          />
                          {renderPreviewImage("ตัวอย่างโลโก้", settings.bill_logo_url)}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-2">
                          <label className="form-label form-label-sm">URL รูป QR Code สำหรับสแกนจ่ายเงิน</label>
                          <input
                            className="form-control form-control-sm"
                            value={settings.bill_qr_image_url}
                            disabled={!isAdmin}
                            onChange={(e) => handleChange("bill_qr_image_url", e.target.value)}
                            placeholder="วางลิงก์รูป QR Code (PromptPay / Wallet ฯลฯ)"
                          />
                          {renderPreviewImage("ตัวอย่าง QR Code", settings.bill_qr_image_url)}
                        </div>
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          className="btn btn-ks-success btn-sm px-4"
                          type="button"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          <i className="fas fa-save me-1"></i>
                          {saving ? "กำลังบันทึก..." : "บันทึกการตั้งค่า"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* CSV Backup (Employees / Services / VIP Members) */}
          <div className="col-lg-4 mb-3">
            <div className="card card-ks-soft h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title mb-3 fw-bold text-secondary">
                  <i className="bi bi-cloud-arrow-down me-2"></i>Backup CSV (พนักงาน / บริการ / VIP)
                </h5>
                <p className="text-muted small mb-2">
                  ระบบสำรองเฉพาะ 3 ส่วนสำคัญในรูปแบบไฟล์ .csv มาตรฐาน: พนักงาน, บริการ/สินค้า, สมาชิก VIP (member_id)
                </p>
                {!isAdmin && (
                  <div className="alert alert-warning py-2">ต้องเป็น admin เท่านั้นจึงจะใช้ Export / Import ได้</div>
                )}
                <div className="list-group mb-3">
                  <button type="button" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" disabled={!isAdmin || exporting} onClick={() => handleExportCsv('employees')}>
                    <span>Export พนักงาน</span>
                    <span className="badge bg-primary rounded-pill">CSV</span>
                  </button>
                  <button type="button" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" disabled={!isAdmin || exporting} onClick={() => handleExportCsv('services')}>
                    <span>Export บริการ / สินค้า</span>
                    <span className="badge bg-primary rounded-pill">CSV</span>
                  </button>
                  <button type="button" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" disabled={!isAdmin || exporting} onClick={() => handleExportCsv('vip_members')}>
                    <span>Export สมาชิก VIP</span>
                    <span className="badge bg-primary rounded-pill">CSV</span>
                  </button>
                </div>
                <hr className="my-3" />
                <h6 className="mb-2">นำเข้าจากไฟล์ CSV</h6>
                <p className="small text-muted">โครงสร้างคอลัมน์ต้องตรงกับไฟล์ที่ export จากระบบ (UTF-8, มี header แถวแรก)</p>
                <ol className="small mb-3">
                  <li>Employees: emp_name, base_salary, position, phone, avatar_url, is_active</li>
                  <li>Services: service_name, price, commission_rate, category, item_type, stock_qty, image_url, is_active</li>
                  <li>VIP: member_id, name, phone, birthday, notes, image_url, created_at</li>
                </ol>
                <div className="mb-2">
                  <label className="form-label form-label-sm">เลือกหมวดที่จะ import</label>
                  <select className="form-select form-select-sm" disabled={!isAdmin || importing} value={importSection} onChange={(e) => setImportSection(e.target.value)}>
                    <option value="employees">Employees</option>
                    <option value="services">Services</option>
                    <option value="vip_members">VIP Members</option>
                  </select>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <input type="file" accept=".csv,text/csv" className="form-control form-control-sm" style={{ maxWidth: 260 }} disabled={!isAdmin || importing} onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                  <button type="button" className="btn btn-ks-outline btn-sm" disabled={!isAdmin || !importFile || importing} onClick={handleImportCsv}>{importing ? 'กำลังนำเข้า...' : 'นำเข้า CSV'}</button>
                  <p className="small text-muted mb-2">ล้างข้อมูลทุกตาราง (เหลือเฉพาะ schema & บัญชี admin) การกระทำนี้ย้อนกลับไม่ได้ พิมพ์คำว่า RESET เพื่อยืนยัน ก่อนทำควร export CSV ทั้ง 3 ส่วน</p>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => { setShowWipeModal(true); setWipeCodeInput(''); }}>
                    <i className="bi bi-trash3 me-1"></i>ล้างข้อมูลทั้งหมด
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      {showWipeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title"><i className="bi bi-trash3 me-2"></i>ยืนยันการล้างข้อมูลทั้งหมด</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWipeModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-danger fw-bold">คำเตือน: ล้างข้อมูลทุกเรคคอร์ด ย้อนกลับไม่ได้!</p>
                <ol className="small mb-3">
                  <li>พนักงาน / คอมมิชชั่น</li>
                  <li>บริการ / หมวดหมู่</li>
                  <li>ออเดอร์ / ธุรกรรม</li>
                  <li>ค่าใช้จ่าย / เคลื่อนไหวเงินสด</li>
                  <li>ส่วนลด / โปรโมชั่น</li>
                  <li>บันทึก backup</li>
                  <li>การจอง / ล็อกการจอง</li>
                  <li>หักเงินพนักงาน</li>
                  <li>ฯลฯ ที่ backend กำหนด</li>
                  <li>บัญชี admin จะยังอยู่</li>
                  <li>ควร backup แบบ FULL ก่อน</li>
                  <li>รหัสยืนยัน = พิมพ์คำว่า RESET</li>
                </ol>
                <div className="mb-2">
                  <label className="form-label">กรอกรหัสยืนยัน (พิมพ์ RESET):</label>
                  <input type="text" className="form-control" value={wipeCodeInput} disabled={wiping} onChange={e => setWipeCodeInput(e.target.value)} placeholder="พิมพ์ RESET" />
                  <div className="form-text">พิมพ์คำว่า RESET (ตัวใหญ่) เพื่อยืนยัน</div>
                </div>
              </div>
              <div className="modal-footer justify-content-between">
                <button type="button" className="btn btn-secondary" disabled={wiping} onClick={() => setShowWipeModal(false)}>ยกเลิก</button>
                <button type="button" className="btn btn-danger" disabled={wiping || wipeCodeInput.trim().toUpperCase() !== expectedWipeCode} onClick={async () => {
                  setWiping(true);
                  try {
                    await api.post('/admin/wipe-all-data', { confirm_code: wipeCodeInput.trim() });
                    addNotification('ล้างข้อมูลทั้งหมดเสร็จแล้ว', 'success');
                    setShowWipeModal(false);
                    loadSettings();
                  } catch (err) {
                    addNotification(err.response?.data?.message || 'ล้างข้อมูลไม่สำเร็จ', 'danger');
                  } finally { setWiping(false); }
                }}>
                  {wiping ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-check-circle me-1"></i>}
                  ยืนยันล้างข้อมูล
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SettingsPage;
